import { CARD_HEIGHT, CARD_WIDTH } from './card-mesh.js';

export const LAYOUT = {
  COL_GAP: 0.18,
  ROW_GAP: 0.4,
  // Each card in a stack lifts by slightly more than CARD_DEPTH so adjacent card
  // faces don't end up coplanar (which causes shadow/z-fighting on tall stacks).
  STACK_LIFT: 0.011,
  FAN_DOWN: 0.27,
  CARD_WIDTH,
  CARD_HEIGHT,
} as const;

export interface PileSlot {
  x: number;
  z: number;
  /** Optional base elevation for the pile's cards. Defaults to 0 (cards rest on the table). */
  y?: number;
  fanDown: boolean;
}

export interface LayoutRow {
  z: number;
  fanDown?: boolean;
  slots: ReadonlyArray<string | null>;
}

export interface LayoutDef {
  cols: number;
  rows: ReadonlyArray<LayoutRow>;
}

export interface Layout {
  slot(pileId: string): PileSlot;
}

export function createLayout(def: LayoutDef): Layout {
  const slotMap = new Map<string, PileSlot>();
  const startX = -((def.cols - 1) / 2) * (LAYOUT.CARD_WIDTH + LAYOUT.COL_GAP);
  for (const row of def.rows) {
    row.slots.forEach((pileId, i) => {
      if (!pileId) return;
      slotMap.set(pileId, {
        x: startX + i * (LAYOUT.CARD_WIDTH + LAYOUT.COL_GAP),
        z: row.z,
        fanDown: !!row.fanDown,
      });
    });
  }
  return {
    slot(pileId) {
      const s = slotMap.get(pileId);
      if (!s) throw new Error(`unknown pile: ${pileId}`);
      return s;
    },
  };
}

// Row positioned `n` rows above (toward the top of the table) from a reference Z.
export function rowAbove(refZ: number, rows = 1): number {
  return refZ - rows * (LAYOUT.CARD_HEIGHT + LAYOUT.ROW_GAP);
}

// Free-form layout for irregular boards (peaks, pyramids, etc.).
export function layoutFromMap(map: Record<string, PileSlot>): Layout {
  return {
    slot(pileId) {
      const s = map[pileId];
      if (!s) throw new Error(`unknown pile: ${pileId}`);
      return s;
    },
  };
}
